import { call, delay, put, select } from 'redux-saga/effects';
import {
  fetchMeFailure,
  fetchMeSuccess,
  fetchThirdPartySSOUrlRequest,
  setThirdPartySSO,
} from '../actions';
import { getCurrentUser, UserLicenceResponse, UserResponse } from '../api';
import { User } from '../models';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { MessageData, ProfilePicture } from 'store/app/types';
import { turnMessageOn } from 'store/app/actions';
import { PICTURE_DELAY_TIME } from 'store/app/constants';
import { getProfilePictures } from 'store/app/selectors';
import { fetchProfilePictureRequest } from 'store/account/actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { getAuthUserLicence, getThirdPartySSO } from '../selectors';
import { UserRights } from 'store/user/models';
import { getCurrentSubscriptionRequest } from 'store/billing/actions';
import { LicenseStatus } from 'store/app/enums';
import { FetchMeRequestAction } from '../types';

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* fetchMeSaga(action: FetchMeRequestAction) {
  const isSessionCheck = action.payload?.isSessionCheck ?? false;

  try {
    const response: UserResponse = yield call(getCurrentUser);
    const userLicence: UserLicenceResponse =
      response?.data?.subscriptionDetails;
    let user: User = response?.data?.user;
    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    // For session checks, we only need to verify the API call succeeded
    // Exit early without doing additional processing
    if (isSessionCheck) {
      yield put(fetchMeSuccess(user, userLicence));
      return;
    }

    const userRights: UserRights[] = [];
    user = {
      ...user,
      userRights,
    };

    const storedThirdPartySSO = localStorage.getItem('thirdPartySSO');
    const thirdPartySSO = yield select(getThirdPartySSO);
    
    // Handle profile picture fetching
    let pp: ProfilePicture[] = yield select(getProfilePictures);
    if (
      user &&
      user.hasProfilePicture &&
      !isLicenseExpired(userLicence)
    ) {
      const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
      if (ppIdx > -1) {
        // Always merge profile picture from app state if available
        const profilePicture = pp[ppIdx].profilePicture;
        user = { ...user, profilePicture };
      } else if (!user.profilePicture) {
        // Only fetch if we don't have it in app state and user doesn't have it
        yield put(fetchProfilePictureRequest(user.userUuid));
        yield delay(PICTURE_DELAY_TIME);
        pp = yield select(getProfilePictures);
        const ppIdx = pp.findIndex((p) => p.userUuid === user.userUuid);
        if (ppIdx > -1) {
          const profilePicture = pp[ppIdx].profilePicture;
          user = { ...user, profilePicture };
        }
      }
    }
    
    // Dispatch fetchMeSuccess ONCE with the updated user
    yield put(fetchMeSuccess(user, userLicence));
    
    // Handle third party SSO after updating user state
    if (thirdPartySSO) {
      yield put(fetchThirdPartySSOUrlRequest(thirdPartySSO));
    } else if (storedThirdPartySSO) {
      yield put(fetchThirdPartySSOUrlRequest(JSON.parse(storedThirdPartySSO)));
    }
    localStorage.removeItem('thirdPartySSO');
    yield put(setThirdPartySSO(undefined));

    // Only fetch subscription if conditions are met
    if (
      shouldFetchSubscription(userLicence) &&
      userLicence?.status !== LicenseStatus.UNLIMITED
    ) {
      yield put(getCurrentSubscriptionRequest(user.accountUuid));
    }
    //yield put(push(routes.main));
    // }
  } catch (error) {
    const isSessionCheck = action.payload?.isSessionCheck ?? false;
    const message: MessageData | null = renderErrorMessage(error);
    
    // For session checks, only logout on actual auth failures (401/403)
    // Don't logout on network errors - user might just be offline temporarily
    if (isSessionCheck) {
      // Only logout if it's an actual authentication error (401/403)
      // Network errors (no status or status !== 401/403) should not clear user state
      if (error?.status === 401 || error?.status === 403 || !message) {
        // Actual auth failure - logout
        yield put(logoutRequest());
        yield put(fetchMeFailure());
      }
      // For network errors during session check, silently fail
      // Don't dispatch fetchMeFailure to avoid clearing user state
      // The user remains logged in and can continue working
      return;
    } else {
      // For regular fetchMe requests, use existing behavior
      if (message) {
        yield put(turnMessageOn(message));
      } else {
        yield put(logoutRequest());
      }
      yield put(fetchMeFailure());
    }
  }
}

// ============= Utility Functions =============

/**
 * Checks if a user license has expired
 * @param userLicence The user license response object
 * @returns true if the license has expired, false otherwise
 */
export const isLicenseExpired = (userLicence: UserLicenceResponse): boolean => {
  if (!userLicence?.expire_date) {
    return true; // If no expire date, consider as expired
  }

  const expireDate = new Date(userLicence.expire_date);
  const todayDate = new Date();

  return expireDate < todayDate;
};

/**
 * Determines whether to fetch subscription based on license dates
 * @param userLicence The user license response object
 * @returns true if subscription should be fetched, false otherwise
 */
export const shouldFetchSubscription = (
  userLicence: UserLicenceResponse
): boolean => {
  if (!userLicence) {
    return false;
  }

  const trialExpiryDate = userLicence.expire_trial
    ? new Date(userLicence.expire_trial)
    : null;
  const nextExpiryDate = userLicence.next_expire_date
    ? new Date(userLicence.next_expire_date)
    : null;
  const expireDate = userLicence.expire_date
    ? new Date(userLicence.expire_date)
    : null;
  const todayDate = new Date();

  // If expiry date has passed today's date, don't fetch
  if (expireDate && expireDate < todayDate) {
    return false;
  }

  // If all three dates exist and are the same, don't fetch
  if (trialExpiryDate && nextExpiryDate && expireDate) {
    const trialTime = trialExpiryDate.getTime();
    const nextTime = nextExpiryDate.getTime();
    const expireTime = expireDate.getTime();

    if (trialTime === nextTime && nextTime === expireTime) {
      return false;
    }
  }

  // In all other cases, fetch the subscription
  return true;
};
